import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Search, LogOut, Plus, Trash2, Upload, DollarSign, ExternalLink, UploadCloud, MousePointer2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  fetchProducts, updateProduct, deleteProduct, uploadImage, uploadBulkImages, bulkPrice, bulkUpdate, getToken, logout,
} from "@/lib/adminApi";
import { formatPrice, CATEGORY_LABELS } from "@/lib/i18n";
import VisualBulkUpload from "@/components/admin/VisualBulkUpload";

const CATS = ["all", "jerseys_countries", "jerseys_clubs", "jerseys_neutral", "cleats", "shoes", "accessories"];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // product being edited
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCat, setBulkCat] = useState("jerseys");
  const [bulkNameFilter, setBulkNameFilter] = useState(""); // "" = all in category (legacy single)
  const [bulkSelectedVariants, setBulkSelectedVariants] = useState([]); // multi-select variant names
  const [bulkVariants, setBulkVariants] = useState([]); // distinct names in current bulkCat
  const [bulkFields, setBulkFields] = useState({
    name_fr: "", name_en: "", price_gnf: "", badge: "", stock: "",
  });
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploadBusy, setBulkUploadBusy] = useState(false);
  const [bulkUploadReport, setBulkUploadReport] = useState(null);
  const [visualUploadOpen, setVisualUploadOpen] = useState(false);

  const hasToken = !!getToken();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchProducts({
        category: category === "all" ? undefined : category,
        q: q || undefined,
        page,
        limit: 30,
      });
      setProducts(r.items);
      setTotal(r.total);
      setTotalPages(r.total_pages);
    } catch {
      toast.error("Erreur de chargement");
    }
    setLoading(false);
  }, [category, q, page]);

  useEffect(() => {
    if (hasToken) load();
  }, [hasToken, load]);

  // Load distinct names for the selected bulk category whenever it changes
  useEffect(() => {
    if (!hasToken || !bulkOpen || !bulkCat) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchProducts({ category: bulkCat, limit: 100, page: 1 });
        const names = Array.from(new Set((r.items || []).map((p) => p.name_fr).filter(Boolean)));
        if (!cancelled) {
          setBulkVariants(names);
          if (bulkNameFilter && !names.includes(bulkNameFilter)) setBulkNameFilter("");
          // drop any selected variants no longer in this category
          setBulkSelectedVariants((prev) => prev.filter((n) => names.includes(n)));
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.error("Failed to load bulk variants:", err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken, bulkOpen, bulkCat]);

  if (!hasToken) return <Navigate to="/admin/login" replace />;

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const onDelete = async (slug) => {
    if (!window.confirm(`Supprimer ${slug} ?`)) return;
    try {
      await deleteProduct(slug);
      toast.success("Produit supprimé");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const onBulkSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    if (bulkFields.name_fr.trim()) payload.name_fr = bulkFields.name_fr.trim();
    if (bulkFields.name_en.trim()) payload.name_en = bulkFields.name_en.trim();
    if (bulkFields.price_gnf !== "") payload.price_gnf = parseInt(bulkFields.price_gnf, 10);
    if (bulkFields.stock !== "") payload.stock = parseInt(bulkFields.stock, 10);
    if (bulkFields.badge === "-") payload.badge = ""; // empty string → clears badge
    else if (bulkFields.badge) payload.badge = bulkFields.badge;
    if (Object.keys(payload).length === 0) {
      toast.error("Renseigne au moins un champ");
      return;
    }
    try {
      const r = await bulkUpdate(
        bulkCat,
        payload,
        undefined,
        bulkSelectedVariants.length ? bulkSelectedVariants : undefined,
      );
      toast.success(`${r.updated} produit(s) mis à jour`);
      setBulkOpen(false);
      setBulkFields({ name_fr: "", name_en: "", price_gnf: "", badge: "", stock: "" });
      setBulkNameFilter("");
      setBulkSelectedVariants([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Load distinct names for the selected bulk category whenever it changes
  // (kept here for clarity; the actual hook is declared near the top so it runs before the conditional return)

  return (
    <div className="min-h-screen" data-testid="admin-products-page">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 border-b border-yellow-600/20 backdrop-blur-md"
        style={{ background: "rgba(5,5,5,0.85)" }}
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/admin" className="flex items-center gap-3">
            <img src="/logos/braf_zak_logo_main.jpeg" alt="" className="w-10 h-10 rounded-full object-cover border border-yellow-500/40" />
            <div>
              <p className="text-[10px] tracking-[0.3em] text-yellow-400/70">ADMIN</p>
              <p className="text-lg font-black" style={{ fontFamily: '"Bebas Neue", system-ui', color: "#F4D77A", letterSpacing: "0.12em" }}>
                BRAF&nbsp;ZAK
              </p>
            </div>
          </Link>
          <nav className="flex items-center gap-2">
            <Link
              to="/admin"
              data-testid="admin-nav-catalog"
              className="text-xs uppercase tracking-widest text-black font-bold px-3 py-2 rounded-full"
              style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
            >
              Catalogue
            </Link>
            <Link
              to="/admin/orders"
              data-testid="admin-nav-orders"
              className="text-xs uppercase tracking-widest text-yellow-200/70 hover:text-yellow-300 px-3 py-2 rounded-full"
            >
              Commandes
            </Link>
            <Link
              to="/admin/lookbook"
              data-testid="admin-nav-lookbook"
              className="text-xs uppercase tracking-widest text-yellow-200/70 hover:text-yellow-300 px-3 py-2 rounded-full"
            >
              Lookbook
            </Link>
            <Link
              to="/"
              target="_blank"
              data-testid="admin-view-site-link"
              className="text-xs uppercase tracking-widest text-yellow-300 hover:text-yellow-200 px-3 py-2 rounded-full border border-yellow-500/30 inline-flex items-center gap-2"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-xs uppercase tracking-widest text-white/70 hover:text-red-300 px-3 py-2 rounded-full border border-white/20 inline-flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Déco
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white" style={{ fontFamily: '"Bebas Neue", system-ui' }}>
              Catalogue
            </h1>
            <p className="text-xs text-yellow-300/70 tracking-widest uppercase mt-1" data-testid="admin-total-count">
              {total} produits
            </p>
          </div>
          <Button
            onClick={() => { setVisualUploadOpen((v) => !v); setBulkUploadOpen(false); }}
            data-testid="visual-upload-toggle"
            className="rounded-full text-black font-bold tracking-widest px-5"
            style={{ background: "linear-gradient(180deg, #BFE3C5, #6DBF7B)" }}
          >
            <MousePointer2 className="w-4 h-4 mr-2" />
            Upload visuel (sans renommage)
          </Button>
          <Button
            onClick={() => { setBulkUploadOpen((v) => !v); setVisualUploadOpen(false); }}
            data-testid="bulk-upload-toggle"
            className="rounded-full text-yellow-300 font-bold tracking-widest px-5 bg-black border border-yellow-500/40 hover:bg-yellow-500/10"
          >
            <UploadCloud className="w-4 h-4 mr-2" />
            Upload par nom de fichier
          </Button>
          <Button
            onClick={() => setBulkOpen((v) => !v)}
            data-testid="bulk-price-toggle"
            className="rounded-full text-black font-bold tracking-widest px-5"
            style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Modifier en masse
          </Button>
        </div>

        {visualUploadOpen && (
          <div className="mb-6">
            <VisualBulkUpload
              onClose={() => setVisualUploadOpen(false)}
              onDone={load}
            />
          </div>
        )}

        {bulkUploadOpen && (
          <div className="mb-6 p-5 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.04]">
            <p className="text-xs uppercase tracking-[0.3em] text-yellow-300 mb-2">Upload de photos en masse</p>
            <p className="text-[12px] text-white/70 mb-3">
              Sélectionnez plusieurs photos d'un coup. Chaque fichier sera <strong className="text-yellow-300">
              automatiquement associé au produit</strong> qui porte le même slug ou nom.
              <br />
              ✅ Exemples de noms de fichier valides :
              <code className="text-yellow-300 mx-1">maillot-original-001.jpg</code>,
              <code className="text-yellow-300 mx-1">access-bandeau.png</code>,
              <code className="text-yellow-300 mx-1">crampons-elite-005.webp</code>
              <br />
              📷 Max 3 photos par produit · Max 5 Mo par fichier · Formats JPG / PNG / WEBP
            </p>
            <input
              type="file"
              accept="image/*"
              multiple
              data-testid="bulk-upload-input"
              disabled={bulkUploadBusy}
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                if (files.length > 50) {
                  toast.error("Maximum 50 fichiers par lot. Recommence avec moins.");
                  e.target.value = "";
                  return;
                }
                setBulkUploadBusy(true);
                setBulkUploadReport(null);
                try {
                  const report = await uploadBulkImages(files);
                  setBulkUploadReport(report);
                  toast.success(`${report.matched.length} photo(s) associée(s) automatiquement`);
                  await load();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Échec de l'upload");
                }
                setBulkUploadBusy(false);
                e.target.value = "";
              }}
              className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-yellow-500 file:text-black hover:file:bg-yellow-400 cursor-pointer"
            />
            {bulkUploadBusy && (
              <p className="text-sm text-yellow-300 mt-3 animate-pulse">⏳ Upload en cours… ne ferme pas cette page.</p>
            )}
            {bulkUploadReport && (
              <div className="mt-4 space-y-2 text-sm">
                <p className="text-green-400">
                  ✅ {bulkUploadReport.matched.length} photo(s) associée(s) sur {bulkUploadReport.total}
                </p>
                {bulkUploadReport.matched.length > 0 && (
                  <ul className="text-white/70 text-xs pl-4 max-h-40 overflow-y-auto">
                    {bulkUploadReport.matched.map((m) => (
                      <li key={`${m.file}-${m.product_slug}`}>• <span className="text-yellow-300">{m.file}</span> → {m.product_name} (photo {m.image_position}/3)</li>
                    ))}
                  </ul>
                )}
                {bulkUploadReport.unmatched.length > 0 && (
                  <>
                    <p className="text-orange-400 mt-2">
                      ⚠️ {bulkUploadReport.unmatched.length} non associé(s) — vérifie le nom de fichier
                    </p>
                    <ul className="text-white/60 text-xs pl-4 max-h-32 overflow-y-auto">
                      {bulkUploadReport.unmatched.map((u) => (
                        <li key={`${u.file}-${u.key}`}>• {u.file} (cherché : <code className="text-orange-300">{u.key}</code>)</li>
                      ))}
                    </ul>
                  </>
                )}
                {bulkUploadReport.errors.length > 0 && (
                  <>
                    <p className="text-red-400 mt-2">❌ {bulkUploadReport.errors.length} erreur(s)</p>
                    <ul className="text-white/60 text-xs pl-4 max-h-32 overflow-y-auto">
                      {bulkUploadReport.errors.map((er) => (
                        <li key={`${er.file}-${er.reason}`}>• {er.file} → {er.reason}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {bulkOpen && (
          <form onSubmit={onBulkSubmit} data-testid="bulk-price-form" className="mb-6 p-5 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.04]">
            <p className="text-xs uppercase tracking-[0.3em] text-yellow-300 mb-4">
              Applique les champs renseignés à tous les produits d'une catégorie. Laisse vide pour ne pas modifier.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-yellow-300 mb-1">Catégorie</label>
                <select
                  value={bulkCat}
                  onChange={(e) => setBulkCat(e.target.value)}
                  data-testid="bulk-category-select"
                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-yellow-500/30 text-white"
                >
                  {CATS.filter((c) => c !== "all").map((c) => (
                    <option key={c} value={c}>{CATEGORY_LABELS[c].fr}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="block text-[10px] uppercase tracking-[0.3em] text-yellow-300 mb-1">
                  Variantes ciblées ({bulkSelectedVariants.length === 0 ? "TOUTES" : `${bulkSelectedVariants.length} cochée(s)`})
                </label>
                <div className="flex flex-wrap gap-2 items-center mb-2">
                  <button
                    type="button"
                    onClick={() => setBulkSelectedVariants(bulkVariants)}
                    data-testid="bulk-select-all"
                    className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-yellow-500/40 text-yellow-300 hover:border-yellow-400"
                  >
                    Tout cocher
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkSelectedVariants([])}
                    data-testid="bulk-select-none"
                    className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/20 text-white/70 hover:border-white/40"
                  >
                    Tout décocher
                  </button>
                  <span className="text-[11px] text-white/50">
                    Aucune cochée = applique à TOUTE la catégorie
                  </span>
                </div>
                <div className="max-h-56 overflow-y-auto p-2 rounded-lg bg-black/40 border border-yellow-500/30 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                  {bulkVariants.length === 0 && (
                    <p className="text-xs text-white/40 italic col-span-full">Chargement…</p>
                  )}
                  {bulkVariants.map((n) => {
                    const checked = bulkSelectedVariants.includes(n);
                    return (
                      <label
                        key={n}
                        data-testid={`bulk-variant-${n.replace(/\s+/g, "-")}`}
                        className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
                          checked ? "bg-yellow-500/20 text-yellow-200" : "text-white/80 hover:bg-white/5"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setBulkSelectedVariants((prev) =>
                              e.target.checked ? [...prev, n] : prev.filter((x) => x !== n),
                            );
                          }}
                          className="accent-yellow-400"
                        />
                        <span className="truncate">{n}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-yellow-300 mb-1">Nom FR</label>
                <input
                  value={bulkFields.name_fr}
                  onChange={(e) => setBulkFields((f) => ({ ...f, name_fr: e.target.value }))}
                  placeholder="ex: Maillot Pro"
                  data-testid="bulk-name-fr-input"
                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-yellow-500/30 text-white"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-yellow-300 mb-1">Nom EN</label>
                <input
                  value={bulkFields.name_en}
                  onChange={(e) => setBulkFields((f) => ({ ...f, name_en: e.target.value }))}
                  placeholder="ex: Pro Jersey"
                  data-testid="bulk-name-en-input"
                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-yellow-500/30 text-white"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-yellow-300 mb-1">Prix (Fg)</label>
                <input
                  type="number"
                  value={bulkFields.price_gnf}
                  onChange={(e) => setBulkFields((f) => ({ ...f, price_gnf: e.target.value }))}
                  placeholder="ex: 450000"
                  data-testid="bulk-price-input"
                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-yellow-500/30 text-white"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-yellow-300 mb-1">Badge</label>
                <select
                  value={bulkFields.badge}
                  onChange={(e) => setBulkFields((f) => ({ ...f, badge: e.target.value }))}
                  data-testid="bulk-badge-select"
                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-yellow-500/30 text-white"
                >
                  <option value="">— Inchangé —</option>
                  <option value="NEW">NEW</option>
                  <option value="BEST">BEST</option>
                  <option value="LIMITED">LIMITED</option>
                  <option value="-">Retirer le badge</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-yellow-300 mb-1">Stock</label>
                <select
                  value={bulkFields.stock}
                  onChange={(e) => setBulkFields((f) => ({ ...f, stock: e.target.value }))}
                  data-testid="bulk-stock-select"
                  className="w-full px-3 py-2 rounded-lg bg-black/40 border border-yellow-500/30 text-white"
                >
                  <option value="">— Inchangé —</option>
                  <option value="99">● En stock</option>
                  <option value="0">○ Rupture</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <Button
                type="submit"
                data-testid="bulk-price-submit"
                className="rounded-full text-black font-bold tracking-widest"
                style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
              >
                {bulkSelectedVariants.length > 0
                  ? `Appliquer aux ${bulkSelectedVariants.length} variante(s) cochée(s)`
                  : "Appliquer à toute la catégorie"}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setBulkOpen(false);
                  setBulkFields({ name_fr: "", name_en: "", price_gnf: "", badge: "", stock: "" });
                  setBulkSelectedVariants([]);
                }}
                className="rounded-full bg-transparent border border-white/20 text-white hover:bg-white/5"
              >
                Annuler
              </Button>
            </div>
          </form>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`admin-filter-${c}`}
              onClick={() => { setCategory(c); setPage(1); }}
              className={`text-xs uppercase tracking-[0.2em] px-3.5 py-2 rounded-full transition-all ${
                category === c ? "text-black" : "text-yellow-200/80 border border-yellow-500/30 hover:border-yellow-400"
              }`}
              style={category === c ? { background: "linear-gradient(180deg, #F4D77A, #D4AF37)" } : undefined}
            >
              {CATEGORY_LABELS[c].fr}
            </button>
          ))}
        </div>

        <form onSubmit={onSearch} className="mb-6 flex gap-2" data-testid="admin-search-form">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-yellow-400/60" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (nom, slug)..."
              data-testid="admin-search-input"
              className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white/5 border border-yellow-500/20 text-white placeholder-white/40 focus:outline-none focus:border-yellow-400 text-sm"
            />
          </div>
          <Button type="submit" className="rounded-full text-black font-bold" style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}>
            Chercher
          </Button>
        </form>

        {/* Product list */}
        {loading ? (
          <p className="text-white/40 text-center py-12">···</p>
        ) : (
          <div className="space-y-3">
            {products.map((p) => (
              <ProductRow
                key={p.id}
                product={p}
                onEdit={() => setEditing(p)}
                onDelete={() => onDelete(p.slug)}
                onToggleStock={async (prod) => {
                  const next = (prod.stock ?? 0) > 0 ? 0 : 99;
                  try {
                    await updateProduct(prod.slug, { stock: next });
                    toast.success(next > 0 ? "Remis en stock" : "Marqué en rupture");
                    load();
                  } catch {
                    toast.error("Erreur");
                  }
                }}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2" data-testid="admin-pagination">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="admin-page-prev"
              className="px-3 py-1.5 rounded-full border border-yellow-500/30 text-yellow-300 disabled:opacity-30 text-sm"
            >
              ← Précédent
            </button>
            <span className="text-yellow-300 text-sm" data-testid="admin-page-info">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              data-testid="admin-page-next"
              className="px-3 py-1.5 rounded-full border border-yellow-500/30 text-yellow-300 disabled:opacity-30 text-sm"
            >
              Suivant →
            </button>
          </div>
        )}
      </main>

      {editing && (
        <EditDrawer
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ProductRow({ product, onEdit, onDelete, onToggleStock }) {
  const hasImage = Array.isArray(product.images) && product.images[0];
  const inStock = (product.stock ?? 0) > 0;
  return (
    <div
      data-testid={`admin-row-${product.slug}`}
      className="flex items-center gap-4 p-3 rounded-xl border border-yellow-500/15 bg-white/[0.02] hover:border-yellow-400/40 transition-colors"
    >
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-black flex items-center justify-center flex-shrink-0 border border-yellow-500/20">
        {hasImage ? (
          <img src={product.images[0]} alt={product.name_fr} className="w-full h-full object-cover" />
        ) : (
          <span className="text-yellow-400/70 font-black" style={{ fontFamily: '"Bebas Neue", system-ui' }}>BZ</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-bold truncate">{product.name_fr}</h3>
        <p className="text-[10px] uppercase tracking-widest text-yellow-300/70 truncate">
          {product.slug} · {product.category}
        </p>
      </div>
      <div className="text-right hidden md:block">
        <p className="text-yellow-300 font-black">{formatPrice(product.price_gnf, "fr")}</p>
        {product.badge && <p className="text-[10px] text-yellow-200/60">{product.badge}</p>}
      </div>
      <button
        onClick={() => onToggleStock(product)}
        data-testid={`admin-stock-toggle-${product.slug}`}
        title={inStock ? "Cliquer pour mettre en rupture" : "Cliquer pour remettre en stock"}
        className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors flex-shrink-0 ${
          inStock
            ? "border-green-400/50 text-green-300 hover:bg-green-500/10"
            : "border-red-400/60 text-red-300 hover:bg-red-500/10"
        }`}
      >
        {inStock ? "● Stock" : "○ Rupture"}
      </button>
      <div className="flex gap-2 flex-shrink-0">
        <button
          onClick={onEdit}
          data-testid={`admin-edit-${product.slug}`}
          className="px-3 py-1.5 rounded-full text-xs font-bold tracking-widest text-black"
          style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
        >
          MODIFIER
        </button>
        <button
          onClick={onDelete}
          data-testid={`admin-delete-${product.slug}`}
          className="w-9 h-9 rounded-full border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500/10"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function EditDrawer({ product, onClose, onSaved }) {
  const [form, setForm] = useState({
    name_fr: product.name_fr || "",
    name_en: product.name_en || "",
    description_fr: product.description_fr || "",
    description_en: product.description_en || "",
    price_gnf: product.price_gnf,
    badge: product.badge || "",
    featured: !!product.featured,
    images: Array.isArray(product.images) ? product.images : [],
    sizes: (product.sizes || []).join(","),
    colors: (product.colors || []).join(","),
    stock: product.stock || 99,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.images.length >= 3) {
      toast.error("Maximum 3 photos par produit. Retire-en une d'abord.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const r = await uploadImage(file);
      setForm((f) => {
        if (f.images.includes(r.url)) return f;
        return { ...f, images: [...f.images, r.url].slice(0, 3) };
      });
      toast.success(`Photo ${Math.min(form.images.length + 1, 3)}/3 uploadée`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (url) =>
    setForm((f) => ({ ...f, images: f.images.filter((x) => x !== url) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch = {
        ...form,
        price_gnf: parseInt(form.price_gnf, 10),
        sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
        stock: parseInt(form.stock, 10) || 99,
        badge: form.badge || null,
      };
      await updateProduct(product.slug, patch);
      toast.success("Produit mis à jour");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      data-testid="admin-edit-drawer"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl h-full overflow-y-auto p-6 border-l border-yellow-500/30"
        style={{ background: "linear-gradient(180deg, #0a0805, #050505)" }}
        data-testid="admin-edit-form"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-black text-yellow-100" style={{ fontFamily: '"Bebas Neue", system-ui' }}>
            Modifier · {product.slug}
          </h2>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none">×</button>
        </div>

        <Section title={`Images (${form.images.length}/3)`}>
          <p className="text-[11px] text-white/50 mb-2">
            Ajoute jusqu'à <strong className="text-yellow-300">3 photos</strong> par produit (face, dos, détail).
            Le client pourra naviguer entre les 3 vues.
          </p>
          <div className="flex flex-wrap gap-3 mb-3">
            {form.images.map((url, idx) => (
              <div key={url} className="relative w-24 h-24 rounded-lg overflow-hidden border border-yellow-500/30 bg-black">
                <img src={url} alt="" className="w-full h-full object-cover" />
                <span className="absolute bottom-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-yellow-400 text-black">
                  {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeImage(url)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white text-xs"
                >×</button>
              </div>
            ))}
          </div>
          {form.images.length < 3 && (
            <label
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-yellow-500/40 text-yellow-300 cursor-pointer hover:border-yellow-400 ${uploading ? "opacity-50" : ""}`}
            >
              <Upload className="w-4 h-4" />
              {uploading ? "Upload..." : `Uploader photo ${form.images.length + 1}/3`}
              <input
                type="file"
                accept="image/*"
                onChange={onUpload}
                disabled={uploading}
                data-testid="admin-image-upload"
                className="hidden"
              />
            </label>
          )}
        </Section>

        <Section title="Nom (FR)">
          <Input value={form.name_fr} onChange={set("name_fr")} testid="admin-name-fr-input" />
        </Section>
        <Section title="Nom (EN)">
          <Input value={form.name_en} onChange={set("name_en")} testid="admin-name-en-input" />
        </Section>
        <Section title="Prix (Fg)">
          <Input type="number" value={form.price_gnf} onChange={set("price_gnf")} testid="admin-price-input" />
        </Section>
        <Section title="Tailles (séparées par virgule)">
          <Input value={form.sizes} onChange={set("sizes")} testid="admin-sizes-input" placeholder="S,M,L,XL" />
        </Section>
        <Section title="Couleurs (laisse vide pour « Couleur unique selon l'image »)">
          <Input
            value={form.colors}
            onChange={set("colors")}
            testid="admin-colors-input"
            placeholder="Or,Noir,Blanc,Rouge"
          />
          <p className="text-[11px] text-white/50 mt-1">
            Sépare par des virgules. Exemple : <code className="text-yellow-300">Or, Noir, Blanc, Rouge</code>.
            Vide = pas de choix de couleur côté client (badge « Couleur unique »).
          </p>
        </Section>
        <Section title="Badge (NEW, BEST, LIMITED ou vide)">
          <Input value={form.badge} onChange={set("badge")} testid="admin-badge-input" />
        </Section>
        <Section title="Description (FR)">
          <textarea
            value={form.description_fr}
            onChange={set("description_fr")}
            rows={3}
            data-testid="admin-desc-fr-input"
            className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-yellow-500/30 text-white resize-none"
          />
        </Section>
        <Section title="Description (EN)">
          <textarea
            value={form.description_en}
            onChange={set("description_en")}
            rows={3}
            data-testid="admin-desc-en-input"
            className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-yellow-500/30 text-white resize-none"
          />
        </Section>
        <label className="flex items-center gap-2 mb-6">
          <input
            type="checkbox"
            checked={form.featured}
            onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
            data-testid="admin-featured-input"
          />
          <span className="text-sm text-white/80">Produit vedette (affiché en page d'accueil)</span>
        </label>

        <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-black/80 border-t border-yellow-500/20 flex gap-3">
          <Button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full border border-white/20 bg-transparent text-white hover:bg-white/5"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={saving}
            data-testid="admin-save-btn"
            className="flex-1 rounded-full text-black font-bold tracking-widest disabled:opacity-50"
            style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
          >
            {saving ? "..." : "ENREGISTRER"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4">
      <label className="block text-[10px] uppercase tracking-[0.3em] text-yellow-300/80 mb-2">
        {title}
      </label>
      {children}
    </div>
  );
}

function Input({ value, onChange, testid, type = "text", placeholder }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-yellow-500/30 text-white focus:outline-none focus:border-yellow-400"
    />
  );
}
